import { normaliseRegistration } from './identity';
import type { FormatParser, ParsedPoint, ParsedTrack } from './types';
import { ParseError } from './types';
import { metresPerSecondToKnots, metresToFeet, parseLooseNumber } from './units';
import {
  attributeOf,
  childNamed,
  descendantNamed,
  descendantsNamed,
  parseXml,
  textOf,
} from './xml';

/**
 * GPX, from SkyDemon, Garmin, a handheld, or anything else.
 *
 * Worth as much as the Flightradar24 path and often worth more:
 * `DATA-SOURCES.md` §2 makes the point that for a NZ GA pilot flying below
 * radar cover an EFB's GPS track is complete, gap-free and starts at engine-on,
 * where ADS-B starts whenever someone's receiver first heard the aircraft.
 *
 * Elevation is metres — GPX is explicit about that — and it is a GPS height,
 * so this is the one parser here that can honestly set `altitudeRef` to `gps`
 * instead of declining to say.
 */
function parseGpx(bytes: Uint8Array): ParsedTrack {
  const doc = parseXml(new TextDecoder().decode(bytes));
  if (descendantNamed(doc, 'gpx') === undefined) {
    throw new ParseError('This XML file is not GPX: it has no <gpx> element.');
  }

  // Track points first. A route or a bare waypoint list is the fallback, since
  // some EFBs export a flown track as a route rather than a track.
  let sources = descendantsNamed(doc, 'trkpt');
  let kind = 'trkpt';
  if (sources.length === 0) {
    sources = descendantsNamed(doc, 'rtept');
    kind = 'rtept';
  }
  if (sources.length === 0) {
    sources = descendantsNamed(doc, 'wpt');
    kind = 'wpt';
  }
  if (sources.length === 0) {
    throw new ParseError('This GPX file contains no track, route or waypoint points.');
  }

  const segments = Math.max(1, descendantsNamed(doc, 'trkseg').length);
  const points: ParsedPoint[] = [];

  for (const element of sources) {
    const ele = parseLooseNumber(textOf(childNamed(element, 'ele')));
    const time = textOf(childNamed(element, 'time'));
    const parsedTime = time === '' ? Number.NaN : Date.parse(time);

    /*
     * Speed and course live in two places across GPX's history: as child
     * elements (1.0, deprecated but still written), and inside
     * `extensions/TrackPointExtension` (1.1, Garmin's). Both are metres per
     * second, so both convert the same way.
     */
    const extensions = childNamed(element, 'extensions');
    const speedElement =
      childNamed(element, 'speed') ??
      (extensions === undefined ? undefined : descendantNamed(extensions, 'speed'));
    const courseElement =
      childNamed(element, 'course') ??
      (extensions === undefined
        ? undefined
        : (descendantNamed(extensions, 'course') ?? descendantNamed(extensions, 'heading')));

    const speedMps = parseLooseNumber(textOf(speedElement));

    points.push({
      tMs: Number.isFinite(parsedTime) ? parsedTime : Number.NaN,
      lat: parseLooseNumber(attributeOf(element, 'lat')),
      lon: parseLooseNumber(attributeOf(element, 'lon')),
      altFt: Number.isFinite(ele) ? metresToFeet(ele) : Number.NaN,
      gsKt: Number.isFinite(speedMps) ? metresPerSecondToKnots(speedMps) : Number.NaN,
      trackDeg: parseLooseNumber(textOf(courseElement)),
    });
  }

  // A registration is not a GPX field, but EFBs put one in the track name and a
  // pilot naming a file after the aeroplane is exactly the common case.
  const name = textOf(descendantNamed(doc, 'name')) || textOf(descendantNamed(doc, 'desc'));
  const registrationMatch = /\b((?:ZK|VH)-?[A-Z]{3})\b/i.exec(name);
  const registration =
    registrationMatch?.[1] === undefined ? undefined : normaliseRegistration(registrationMatch[1]);

  return {
    source: 'gpx',
    points,
    altitudeRef: 'gps',
    ...(registration !== undefined ? { registration } : {}),
    warnings: [],
    notes: [
      `${String(points.length)} ${kind} points across ${String(segments)} segment${
        segments === 1 ? '' : 's'
      }`,
      ...(name === '' ? [] : [`Track name: ${name}`]),
    ],
  };
}

export const gpxParser: FormatParser = {
  format: 'gpx',
  label: 'GPX',
  extensions: ['.gpx'],
  sniff: (text) => /<gpx[\s>]/i.test(text.slice(0, 4000)),
  parse: parseGpx,
};
